<script lang="ts">
    import type { Unsubscriber } from "svelte/store";
    import type { AvailabilityStatus } from "@workadventure/messages";
    import { onDestroy, onMount } from "svelte";
    import { wokaMenuStore, wokaMenuProgressStore } from "../../Stores/WokaMenuStore";
    import ButtonClose from "../Input/ButtonClose.svelte";
    import VisitCard from "../VisitCard/VisitCard.svelte";
    import WokaFromUserId from "../Woka/WokaFromUserId.svelte";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import LL from "../../../i18n/i18n-svelte";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import { getColorHexOfStatus } from "../../Utils/AvailabilityStatus";
    import type { WokaMenuAction, WokaMenuData } from "../../Stores/WokaMenuStore";
    import { startMovingEventName } from "../../Phaser/Player/Player";

    let wokaMenuData: WokaMenuData | undefined = $state();
    let sortedActions: WokaMenuAction[] | undefined = $state();
    let remotePlayer: { chatID?: string; availabilityStatus: AvailabilityStatus } | undefined = $state();

    let wokaMenuStoreUnsubscriber: Unsubscriber | null;

    function onKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") {
            closeActionsMenu();
        }
    }

    function closeActionsMenu() {
        wokaMenuStore.clear();

        // At the end of the actions menu, emit the ask position message to the server
        const currentScerne = gameManager.getCurrentGameScene();
        currentScerne.CurrentPlayer.emitAskPosition();
    }

    let buttonsLayout: "row" | "column" | "wrap" = $state("row");

    wokaMenuStoreUnsubscriber = wokaMenuStore.subscribe((value) => {
        wokaMenuData = value;
        if (wokaMenuData) {
            remotePlayer = gameManager
                .getCurrentGameScene()
                .getRemotePlayersRepository()
                .getPlayers()
                .get(wokaMenuData.userId);
            sortedActions = [...wokaMenuData.actions.values()].sort((a, b) => {
                const ap = a.priority ?? 0;
                const bp = b.priority ?? 0;
                if (ap > bp) {
                    return -1;
                }
                if (ap < bp) {
                    return 1;
                } else {
                    return 0;
                }
            });
            const nbButtons = sortedActions.length + (wokaMenuData.wokaName ? 0 : 1) + (remotePlayer?.chatID ? 1 : 0);
            if (nbButtons < 4) {
                buttonsLayout = "row";
            } else {
                buttonsLayout = "wrap";
            }
        }
    });

    const onStartMoving = () => {
        wokaMenuStore.clear();
    };

    onMount(() => {
        gameManager.getCurrentGameScene().CurrentPlayer?.on(startMovingEventName, onStartMoving);
    });

    onDestroy(() => {
        gameManager.getCurrentGameScene().CurrentPlayer?.off(startMovingEventName, onStartMoving);
        if (wokaMenuStoreUnsubscriber) {
            wokaMenuStoreUnsubscriber();
        }
    });
</script>

<svelte:window onkeydown={onKeyDown} />

{#if wokaMenuData}
    <div
        class="m-auto my-0 h-fit min-h-fit max-w-lg min-w-8 max-sm:max-w-[89%] z-350 bg-mlight/80 shadow-light transition-all
            backdrop-blur rounded-md2 pointer-events-auto p-5 pb-2 text-mdark relative"
        data-testid="actions-menu"
    >
        <div class="absolute top-0 -right-14 max-sm:top-2 max-sm:right-2">
            <ButtonClose onclick={closeActionsMenu} />
        </div>
        <div class="w-full bg-cover border-b border-y-0 border-x-0 border-mblue border-solid pb-4">
            <div class="flex items-center justify-center">
                <div class="flex flex-col justify-center items-center font-bold text-xl">
                    {#if wokaMenuData.userId != undefined && wokaMenuData.userId != -1}
                        <div
                            id="woka"
                            class="overflow-hidden w-fit h-fit rounded-full cursor-not-allowed bg-[rgb(103,185,133)]"
                        >
                            <WokaFromUserId
                                userId={wokaMenuData.userId}
                                placeholderSrc="/assets/placeholder-woka.svg"
                                customWidth="3.75rem"
                            />
                        </div>
                    {/if}
                    <div class="w-max font-medium text-base leading-7 flex items-center">
                        {#if remotePlayer}
                            <div
                                class="aspect-square h-2 w-2 rounded-full me-1"
                                style="background-color: {getColorHexOfStatus(remotePlayer.availabilityStatus)}"
                            ></div>
                        {/if}
                        {wokaMenuData.wokaName}
                    </div>
                </div>
            </div>

            {#if wokaMenuData.visitCardUrl}
                <VisitCard visitCardUrl={wokaMenuData.visitCardUrl} isEmbedded={true} showSendMessageButton={false} />
            {/if}

            {#if $wokaMenuProgressStore}
                <div class="px-4 pb-4 pt-2">
                    <div class="w-full bg-white/10 rounded-full h-2 mb-2">
                        <div
                            class="bg-primary h-2 rounded-full transition-all duration-300"
                            style="width: {$wokaMenuProgressStore.progress}%"
                        ></div>
                    </div>
                    <p class="text-white/80 text-sm text-center animate-pulse">
                        {$wokaMenuProgressStore.message}
                    </p>
                </div>
            {/if}
        </div>

        {#if sortedActions}
            <div
                class="flex flex-row items-center w-full justify-center mt-2 gap-0 max-[420px]:flex-col {buttonsLayout ===
                'wrap'
                    ? 'min-[420px]:flex-wrap'
                    : ''}"
                class:margin-close={!wokaMenuData.wokaName}
            >
                {#each sortedActions ?? [] as action (action.uuid)}
                    <button
                        type="button"
                        data-testid={action.testId}
                        class="btn btn-light btn-ghost text-nowrap text-sm justify-center m-0 px-3! min-w-0 max-[420px]:w-full {action.style ??
                            ''} group"
                        class:mx-2={buttonsLayout === "column"}
                        onclick={(event) => {
                            analyticsClient.trackAdminEvent("map_editor.property.clicked", {
                                name: action.actionName,
                                style: action.style,
                            });
                            event.preventDefault();
                            closeActionsMenu();
                            action.callback();
                        }}
                    >
                        <span class="flex flex-row gap-1 items-center justify-center">
                            {#if action.actionIcon && typeof action.actionIcon === "string"}
                                <div class="w-4 h-4">
                                    <img src={action.actionIcon} class="w-full h-full" alt="" />
                                </div>
                            {:else if action.actionIcon && typeof action.actionIcon === "function"}
                                {@const ActionIcon = action.actionIcon}
                                <ActionIcon height="h-4" width="w-4" />
                            {/if}
                            {action.actionName}
                        </span>
                    </button>
                {/each}

                {#if !wokaMenuData.wokaName}
                    <button
                        type="button"
                        class="btn btn-light btn-ghost text-nowrap justify-center my-2 mx-1 w-fit"
                        onclick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            closeActionsMenu();
                        }}
                    >
                        {$LL.actionbar.close()}
                    </button>
                {/if}
            </div>
        {/if}
    </div>
{/if}
